/* eslint-disable camelcase */
import { isImmutable, List, Map, Record } from 'immutable';
import moment from 'moment';
import momentTZ from 'moment-timezone';
import qs from 'query-string';
import r from 'render-dom';
import { isURL } from 'validator';

import { ColumnsInfo, SourceTypes } from 'constants/columnInfo';
import { displayNames } from 'constants/plans';
import { addTimedAlert } from 'components/shared/Alert';
import Spinner from 'components/shared/Spinner';
import { marketplaces } from 'constants/marketplaces';
import { history } from 'router';
import { getJwt } from 'utils/jwt';
import {
    ARCHIVE,
    END,
    MIN_REBATE,
    MIN_RESEARCH_REBATE,
    MIN_REWARD_REBATE,
    PAUSE,
    START,
    STATE,
    STATUS,
} from 'constants/campaigns';
import { STATUS as ORDER_STATUS } from 'constants/orders';
import {
    archiveCampaign,
    endCampaign,
    pauseCampaign,
    startCampaign,
} from 'redux/campaign/actions';

export const CampaignCosts = Record({
    baseFee: 0,
    dailyFees: 0,
    dailyRebates: 0,
    feePerSnag: 0,
    rebateAmount: 0,
    totalFees: 0,
    totalPerCampaign: 0,
    totalPerDay: 0,
    totalPerRebate: 0,
    totalRebates: 0,
}, 'CampaignCosts');

const { ELITE, FULL_SERVICE, PAY_AS_YOU_GO, PLUS, PRO, STARTER } = displayNames;
const displayToPlanNames = Map({
    [ELITE]: ['Prestige', 'Snagshout Elite', 'Snagshout Elite Yearly', 'Snagshout Elite Annual'],
    [FULL_SERVICE]: ['Full Service Monthly', 'Full Service Bi Annual'],
    [PAY_AS_YOU_GO]: ['Pay As You Go'],
    [PLUS]: ['Snagshout Plus', 'Snagshout Plus Annual'],
    [PRO]: [
        'Professional',
        'Professional Annual',
        'Professional Yearly',
        'Snagshout Pro',
        'Snagshout Pro Annual',
    ],
    [STARTER]: ['Starter', 'Starter Annual', 'Starter Yearly'],
});

export const parseQueryString = (query) => {
    return qs.parse(query);
};

export const updateQueryString = (newQuery) => {
    const currentQuery = parseQueryString(window.location.search);

    history.push(`${window.location.pathname}?${qs.stringify({ ...currentQuery, ...newQuery })}`);
};

export const bool = (expression, truthy, falsey) => {
    return expression ? truthy : falsey;
};

export const componentOr = bool;

export const componentOrNull = (expression, truthy) => bool(expression, truthy, null);

export const componentOrSpinner = (expression, truthy) => bool(expression, truthy, r(Spinner));

// updates pivot fields in lists to reflect current sort order
export const updateSortOrder = (list) => list.map(
    (item, index) => {
        return item.setIn(['pivot', 'sort'], index)
            .setIn(['pivot', 'primary'], Number(!index));
    }
);

export const getMediumImageUrl = (medium, slug, strategy, width, height) => {
    if (!medium) {
        return '';
    }

    if (medium.get('type') === 'YOUTUBE') {
        return `https://i1.ytimg.com/vi/${medium.getIn(['metadata', 'id'])}/hqdefault.jpg`;
    }
    const id = medium.get('id');
    const ext = medium.getIn(['metadata', 'extension'], 'jpg');

    let dimensions = width;

    if (height) {
        dimensions = `${dimensions}/${height}`;
    }

    return `//${process.env.REACT_APP_IMAGE_API_URL}/${strategy}/${dimensions}/${id}-${slug}.${ext}`;
};

export const getPrimaryImage = (media) => {
    if (media.size) {
        const primaryImage = media.filter((medium) => medium.getIn(['pivot', 'primary'], false)).first();
        const defaultImage = media.first();

        return primaryImage || defaultImage;
    }

    return null;
};

// This function will give you back a status if you pass a state or state if you pass a status
export const getCampaignStatus = (campaign) => {
    const campaignState = campaign.get('state');
    const isPending = campaign.getIn(['applications', 0, 'pivot', 'state']) === STATUS.PENDING;
    const isOver = moment().isAfter(moment(campaign.get('endsAt')));
    let published = STATUS.LIVE;

    if (isPending) {
        published = STATUS.PENDING;
    }
    if (isOver) {
        published = STATUS.ENDED;
    }
    const statuses = [
        [STATE.ARCHIVED, STATUS.ARCHIVED],
        [STATE.DRAFT, STATUS.DRAFT],
        [STATE.PAUSED, STATUS.PAUSED],
        [STATE.PUBLISHED, published],
        [STATE.STOPPED, STATUS.ENDED],
    ];

    const [state, status] = statuses.filter((tup) => {
        return tup.includes(campaignState);
    }).pop();

    return state === campaignState ? status : state;
};

export const getCampaignActions = (status) => {
    const archive = {
        action: archiveCampaign,
        name: ARCHIVE,
    };
    const end = {
        action: endCampaign,
        name: END,
    };
    const pause = {
        action: pauseCampaign,
        name: PAUSE,
    };
    const start = {
        action: startCampaign,
        name: START,
    };

    switch (status) {
        case STATUS.PAUSED:
            return [start, end];

        case STATUS.ARCHIVED:
            return [];

        case STATUS.ENDED:
            return [archive];

        case STATUS.DRAFT:
            return [end];

        default:
            return [pause, end];
    }
};

export const getStatusColor = (previousData, currentData) => {
    if (currentData > previousData) {
        return '#03DE74';
    }
    if (currentData < previousData) {
        return '#ff000a';
    }
    return '#ff000a';
};

export const getStatusActions = (campaign) => {
    const status = getCampaignStatus(campaign);

    return {
        actions: getCampaignActions(status),
        status,
    };
};

export const formatDateToPST = (dateString) => {
    if (dateString) {
        return momentTZ.tz(dateString, 'America/Los_Angeles').format('M/D/Y');
    }

    return '--';
};

export const formatPriceIfNeeded = (val) => {
    if (val && !Number.isNaN(parseFloat(val)) && val % 1 !== 0) {
        return Number(val).toFixed(2);
    }

    return val;
};

export function getDiscountPrice(campaign) {
    const discountPrice = campaign.get('discountPrice');

    if (discountPrice > 0) {
        return `$${formatPriceIfNeeded(discountPrice)}`;
    }

    return '$0';
}


export const formatThousands = (num) => {
    if (num >= 1000) {
        const numInThousands = num / 1000;
        const digitAfterDecimal = Math.trunc(numInThousands * 10) % 10;
        const decimal = digitAfterDecimal ? `.${digitAfterDecimal}` : '';

        return `${Math.trunc(numInThousands)}${decimal}k`;
    }

    return `${num}`;
};

export const scrollToTop = () => window.scrollTo(0, 0);

export const isNumber = (n) => !Number.isNaN(parseFloat(n)) && Number.isFinite(n);

export const renderInfinity = (n) => (n === -1 ? '∞' : n);

export const targetBlank = (href) => ({ href, rel: 'noopener noreferrer', target: '_blank' });

export function createSymbols(constants) {
    const symbols = {};

    constants.forEach((constant) => {
        symbols[constant] = Symbol(constant);
    });

    return symbols;
}

export function createCollectionSymbols(name) {
    return {
        // TODO: Remove these "DELETE" constants (should be on the resource not the collection)
        DELETE: `DELETE_${name}`,
        DELETE_FAIL: `DELETE_${name}_FAIL`,
        DELETE_SUCCESS: `DELETE_${name}_SUCCESS`,
        EDIT_IN: `EDIT_IN_${name}_COLLECTION`,
        FETCH: `FETCH_${name}_COLLECTION`,
        FETCH_FAIL: `FETCH_${name}_COLLECTION_FAIL`,
        FETCH_SUCCESS: `FETCH_${name}_COLLECTION_SUCCESS`,
        POST: `POST_${name}`,
        POST_FAIL: `POST_${name}_FAIL`,
        POST_SUCCESS: `POST_${name}_SUCCESS`,
        RESET: `RESET_${name}`,
    };
}

export function createResourceSymbols(name) {
    return {
        DELETE: `DELETE_${name}`,
        DELETE_FAIL: `DELETE_${name}_FAIL`,
        DELETE_SUCCESS: `DELETE_${name}_SUCCESS`,
        EDIT_IN: `EDIT_IN_${name}`,
        FETCH: `FETCH_${name}`,
        FETCH_FAIL: `FETCH_${name}_FAIL`,
        FETCH_SUCCESS: `FETCH_${name}_SUCCESS`,
        PATCH: `PATCH_${name}`,
        PATCH_FAIL: `PATCH_${name}_FAIL`,
        PATCH_SUCCESS: `PATCH_${name}_SUCCESS`,
        PUT: `PUT_${name}`,
        PUT_FAIL: `PUT_${name}_FAIL`,
        PUT_SUCCESS: `PUT_${name}_SUCCESS`,
        RESET: `RESET_${name}`,
    };
}

export const handleFormProps = ({ reset, submitSucceeded }) => {
    if (submitSucceeded) {
        reset();
    }
};

export const getPlanFeatures = (plan) => {
    const convertValue = (value) => value > -1 ? value : Infinity;
    const checkUnlimited = (val) => Number.isFinite(val) ? val : 'Unlimited';
    const boost = convertValue(plan.get('boosts'));
    const campaigns = convertValue(plan.get('keywords'));
    const videos = convertValue(plan.get('videos'));
    const keywords = convertValue(plan.get('keywords'));

    return [
        `${checkUnlimited(campaigns)} campaign${campaigns > 1 ? 's' : ''}`,
        `${checkUnlimited(videos)} video${videos !== 1 ? 's' : ''} per month`,
        `${checkUnlimited(keywords)} products tracked`,
        boost ? `${checkUnlimited(boost)} keyword boost${boost !== 1 ? 's' : ''}` : '',
        plan.get('followups') ? 'Email shoppers' : '',
        'Unlimited landing pages',
        plan.get('chat') ? 'Live chat with support' : '',
    ];
};

export const trackCampaignValues = ({ id, name, state, type }) => ({ id, name, state, type });

export const addOrdinalSuffix = (n) => {
    // eslint-disable-next-line
    const suffix = ['', 'st', 'nd', 'rd'][n % 100 >> 3 ^ 1 && n % 10] || 'th';

    return `${n}${suffix}`;
};

export const getAsinFromURL = (url) => {
    if (isURL(url)) {
        const match = url.match(/amazon\.[a-z.]+\/([\w-]+\/)?(dp|gp\/product)\/(\w+\/)?(\w{10})/);

        return match ? match[4] : null;
    }

    return null;
};

export const getAsinFromSearch = (search) => {
    const asin = getAsinFromURL(search);

    if (asin) {
        return asin;
    }

    if (search.length === 10) {
        return search;
    }

    return null;
};

export const getBoostFields = (boost) => {
    const fields = boost.get('fields', Map({})) || Map({});

    return {
        brand: fields.get('brand', '') || '',
        ean: fields.get('ean', '') || '',
        merchant: fields.get('merchant', '') || '',
        queries: boost.get('queries', List([''])) || List(['']),
        tos: fields.get('tos', false),
        weights: boost.get('weights', List([''])) || List(['']),
    };
};

export const getBoostUrlFromQuery = ({ asin, boost, query }) => {
    const { brand, ean, merchant } = getBoostFields(boost);
    const keywordString = `?k=${encodeURIComponent(query).replace(/%20/g, '+')}`;
    const brandString = brand ? `&field-brand=${encodeURIComponent(brand).replace(/%20/g, '+')}` : '';
    const eanString = ean ? `&field-ean=${ean}` : '';
    const asinString = asin ? `&hidden-keywords=${asin}` : '';
    const merchantString = merchant ? `&merchant=${merchant}` : '';
    const fields = [
        keywordString,
        brandString,
        eanString,
        asinString,
        merchantString,
    ].join('');
    const url = `https://www.amazon.com/s${fields}&rh=p_n_condition-type%3A6461716011&dc`;

    return url.split(' ').join('+');
};

/* eslint-disable */
export const hideChat = () => window.zE && window.zE('webWidget', 'hide');
export const showChat = () => window.zE && window.zE('webWidget', 'show');
/* eslint-enable */

export const getReportDefaultFilter = () => {
    const lastMonth = moment()
        .startOf('month')
        .subtract(1, 'days');

    return qs.stringify({ month: lastMonth.format('M'), year: lastMonth.get('year') });
};

export const openInNewTab = (url) => {
    const a = document.createElement('a');
    a.target = '_blank';
    a.href = url;
    a.click();
};

export const copyToClipboard = (text) => {
    const id = `copy-input-${Date.now()}`;
    const textarea = document.createElement('textarea');
    textarea.id = id;
    textarea.value = text;
    textarea.style.top = 0;
    textarea.style.left = 0;
    textarea.style.padding = 0;
    textarea.style.width = '1px';
    textarea.style.height = '1px';
    textarea.style.border = 'none';
    textarea.style.outline = 'none';
    textarea.style.boxShadow = 'none';
    textarea.style.position = 'fixed';
    textarea.style.background = 'transparent';
    document.querySelector('body').appendChild(textarea);

    const appendedTextArea = document.getElementById(id);

    appendedTextArea.select();

    try {
        const url = document.execCommand('copy');
        if (!url) {
            addTimedAlert({ message: 'Unable to copy url' });
        } else {
            addTimedAlert({ message: 'URL Copied!' });
        }
    } catch (err) {
        addTimedAlert({ message: 'Unable to copy url' });
    }
};

export const getDiscountPercentage = (discountPrice, listingPrice) => {
    return Number(listingPrice) ? Math.ceil(100 * (1 - (discountPrice / listingPrice))) : 100;
};

export const getCampaignIcon = (campaign) => {
    const campaignState = campaign.get('state');
    const campaignStatus = getCampaignStatus(campaign);
    const ended = {
        color: '#999',
        icon: 'ended',
    };

    if (campaignState === STATE.PUBLISHED) {
        if (moment().isAfter(campaign.get('endsAt'))) {
            return ended;
        }
    }

    if (campaignStatus === STATUS.LIVE && moment().isBefore(campaign.get('startsAt'))) {
        return {
            color: '#999',
            icon: 'pending',
        };
    }

    const icons = {
        [STATUS.ARCHIVED]: {
            color: '#000',
            icon: 'archived',
        },
        [STATUS.DRAFT]: {
            color: '#ffc107',
            icon: 'draft',
        },
        [STATUS.ENDED]: ended,
        [STATUS.LIVE]: {
            color: 'red',
            icon: 'live',
        },
        [STATUS.PAUSED]: {
            color: 'blue',
            icon: 'paused',
        },
        [STATUS.PENDING]: {
            color: '#999',
            icon: 'pending',
        },
    };

    return icons[campaignStatus] || { color: '', icon: '' };
};

export const getRebateIcon = (order) => {
    const status = order.get('status');
    const icons = {
        [ORDER_STATUS.COMPLETED]: {
            color: '#03DE74',
            icon: 'paid',
        },
        [ORDER_STATUS.DISPUTED]: {
            color: 'red',
            icon: 'rejected',
        },
        [ORDER_STATUS.EXPIRED]: {
            color: 'red',
            icon: 'expired',
        },
        [ORDER_STATUS.NEW]: {
            color: 'orange',
            icon: 'new',
        },
        [ORDER_STATUS.PENDING]: {
            color: '#999',
            icon: 'pending',
        },
        [ORDER_STATUS.REJECTED]: {
            color: 'red',
            icon: 'rejected',
        },
        [ORDER_STATUS.RETURNED]: {
            color: '#999',
            icon: 'returned',
        },
    };

    const { color, icon } = icons[status] || { color: '', icon: '' };
    const style = { textTransform: 'capitalize' };

    function getText() {
        if (!order.get('isPaid') && status === ORDER_STATUS.COMPLETED) {
            return (
                r.span({ style }, ['completed'])
            );
        } else if (status === ORDER_STATUS.DISPUTED) {
            return (
                r.span({ style }, ['disputed'])
            );
        }

        return (
            r.span({ style }, [icon])
        );
    }

    return (
        r.span({ style: { alignItems: 'center', display: 'inline-flex', fontSize: 12 } }, [
            r.i({ className: `icon-${icon}`, style: { color, display: 'flex', fontSize: 16 } }),
            getText(),
        ])
    );
};
export const isLoggedIn = () => {
    return Boolean(getJwt());
};

export const getErrorMessage = (obj) => {
    const message = obj[Object.keys(obj)[0]];
    return Array.isArray(message) ? message[0] : message;
};

export const sortHelper = (list, field, direction) => {
    return list.sort((a, b) => {
        let fieldA = a.get(field);
        let fieldB = b.get(field);

        if (field === 'ranking') {
            fieldA = fieldA === -1 ? 9998 : fieldA;
            fieldB = fieldB === -1 ? 9998 : fieldB;
            fieldA = fieldA === null ? 9999 : fieldA;
            fieldB = fieldB === null ? 9999 : fieldB;
        }

        if (fieldA > fieldB) {
            return direction === 'asc' ? 1 : -1;
        }

        if (fieldA < fieldB) {
            return direction === 'asc' ? -1 : 1;
        }

        return 0;
    });
};

export function getPlanDisplayName(planName) {
    // eslint-disable-next-line no-unused-vars
    const nameMatch = displayToPlanNames.findKey((planNames) => {
        return planNames.includes(planName);
    });

    return nameMatch || 'Custom';
}

export function mapPlansToDisplayName(plans) {
    return displayToPlanNames.map((planNames) => {
        return plans.filter((p) => planNames.includes(p.get('name')));
    });
}

export function truncateString(string, numChars) {
    return string.length > numChars ? `${string.slice(0, numChars)}...` : string;
}

export const createOptions = (isAmazon) => {
    const amazon = ColumnsInfo.filter((elem) => elem.source === SourceTypes.Amazon);
    const google = ColumnsInfo.filter((elem) => elem.source === SourceTypes.Google);
    const opt = isAmazon ? amazon : google;
    return opt.map((el) => el.title);
};

export const getColumnInfo = (id) => {
    return ColumnsInfo.find((elem) => elem.id === id);
};

export const getMarketplaceData = (product) => {
    const listings = product.get('listings', List());
    const listing = listings.find((l) => l.get('isMonitored')) || listings.first();
    const marketplaceURL = listing?.get('marketplace');
    const marketplace = marketplaces.find((m) => m.value.includes(marketplaceURL));
    const marketplaceName = marketplace ? marketplace.label : 'Go to product';

    return {
        isAmazonProd: marketplaceURL?.includes('amazon'),
        listingURL: listing?.get('url'),
        marketplaceName,
    };
};

export const getListingMarketplace = (listings, marketplace) => {
    if (listings && listings.size) {
        return listings.find((l) => l?.get('marketplace').includes(marketplace));
    }

    return undefined;
};

export const getMarketplaceName = (listing, availableMarketplaces = marketplaces) => {
    if (listing && listing.get('marketplace')) {
        const marketplace = availableMarketplaces.find((m) => m.value.includes(listing.get('marketplace')));
        return marketplace ? marketplace.label
            : 'Your custom website';
    }

    return undefined;
};

export const getMarketplaceHint = (listing) => {
    const marketplace = marketplaces.find((m) => m.value.includes(listing?.get('marketplace')));
    return marketplace?.marketplaceIdHint || undefined;
};

export const getReviewUrl = (listing) => {
    const marketplace = marketplaces.find((m) => m.value.includes(listing?.get('marketplace')));
    const reviewUrl = typeof marketplace?.reviewURL === 'string'
        ? marketplace?.reviewURL
        : marketplace?.reviewURL && marketplace?.reviewURL(listing);
    return reviewUrl || '';
};

// Helps to handle old and new surveys
export const mapQuestionsToAnswers = (questions, answers) => {
    if (answers.countBy((answer) => isImmutable(answer)).get(true) === answers.size) {
        return answers.toList();
    }
    const mappedQuestions = questions.map((question, index) => {
        return question.set('answer', answers.getIn([`${index + 1}`, 'answer'], answers.get(`${index + 1}`)));
    });
    if (questions.size < answers.size && isImmutable(answers.last()) && answers.last().has('imageUrls')) {
        const images = Map({ answer: answers.last().get('imageUrls'), label: 'Review screenshot', type: 'image' });
        return mappedQuestions.push(images).toList();
    }
    return mappedQuestions.toList();
};

export function getCouponPrices(campaign, plan) {
    const isPublished = campaign.get('state') === 'PUBLISHED';
    const remainingQuantity = campaign.get('remainingQuantity');
    const pricePerSnag = plan.get('pricePerSnag');

    if (campaign.getIn(['multiUsePromotion', 'type']) === 'MULTI_CODE') {
        const quantity = isPublished ? remainingQuantity : campaign.get('limit');
        const totalFees = Math.round(pricePerSnag * quantity * 100) / 100;

        return CampaignCosts({
            baseFee: pricePerSnag,
            feePerSnag: pricePerSnag,
            totalFees,
            totalPerCampaign: totalFees,
            // coupons don't have daily hold
            totalPerDay: 0,
        });
    }

    const numberSingleUse = isPublished ? remainingQuantity : campaign.get('singleUsePromotions')?.size;

    if (numberSingleUse) {
        const totalFees = Math.round(pricePerSnag * numberSingleUse * 100) / 100;

        return CampaignCosts({
            baseFee: pricePerSnag,
            feePerSnag: pricePerSnag,
            totalFees,
            totalPerCampaign: totalFees,
            // coupons don't have daily hold
            totalPerDay: 0,
        });
    }

    return {};
}

export function getSurveyPrice(marketplace, plan, pro = false) {
    const typeofMarketplace = typeof marketplace;
    const pricePerActionRebate = plan.get('pricePerActionRebate') || 10;
    const pricePerSurveyActionRebate = plan.get('pricePerSurveyActionRebate');

    if (pro) {
        if (typeofMarketplace === 'string') {
            if (marketplace.includes('amazon')) {
                return plan.get('pricePerProAZSurveyActionRebate');
            }

            if (['apple', 'google'].find((v) => marketplace.includes(v))) {
                return plan.get('pricePerAppSurveyActionRebate') || pricePerSurveyActionRebate;
            }
            return plan.get('pricePerProSurveyActionRebate');
        }

        if (typeofMarketplace === 'object' && marketplace.value) {
            return getSurveyPrice(marketplace.value, plan, pro);
        }
    } else {
        if (typeofMarketplace === 'string') {
            if (marketplace.includes('amazon')) {
                return plan.get('pricePerAZSurveyActionRebate');
            }

            if (['apple', 'google'].find((v) => marketplace.includes(v))) {
                return plan.get('pricePerAppSurveyActionRebate') || pricePerSurveyActionRebate;
            }
            return plan.get('pricePerSurveyActionRebate');
        }

        if (typeofMarketplace === 'object' && marketplace.value) {
            return getSurveyPrice(marketplace.value, plan);
        }
    }

    return pricePerActionRebate;
}

export const getMinRebateAmount = (campaign) => {
    const isPro = campaign.getIn(['action', 'isPro'], false);
    const action = campaign.getIn(['action', 'type'], false);
    if (isPro) {
        return campaign.getIn(['listing', 'price'], 0);
    }
    switch (action) {
        case 'RESEARCH': return MIN_RESEARCH_REBATE;
        case 'REWARD': return MIN_REWARD_REBATE;
        default: return MIN_REBATE;
    }
};

export function getPricePerRebate(campaign, plan) {
    const marketplace = campaign.getIn(['listing', 'marketplace'], '');
    const actionType = campaign.getIn(['action', 'type']);
    const isPro = campaign.getIn(['action', 'isPro']);
    const pricePerActionRebate = plan.get('pricePerActionRebate');

    const getPricingValue = () => {
        const formattedAction = actionType[0] + actionType.substring(1).toLowerCase();
        if (isPro) {
            return plan.get(`pricePerPro${formattedAction}ActionRebate`);
        }
        return plan.get(`pricePer${formattedAction}ActionRebate`);
    };

    if (actionType) {
        const promotionType = campaign.getIn(['multiUsePromotion', 'type']);

        if (promotionType === 'REWARD_REBATE') {
            return plan.get('pricePerRewardRebate');
        }

        if (actionType === 'SURVEY') {
            return getSurveyPrice(marketplace, plan, isPro);
        }

        const price = getPricingValue();

        return price || pricePerActionRebate;
    }

    return plan.get('pricePerRebate');
}

export function getRebatePrices(campaign, plan, customCampaignFee = Infinity) {
    const isRewardRebate = campaign.getIn(['multiUsePromotion', 'type']) === 'REWARD_REBATE';
    const dailyLimit = campaign.get('dailyLimit');
    const isResearchCampaign = campaign.getIn(['action', 'type']) === 'RESEARCH';
    const rebateAmount = Number(campaign.getIn(['multiUsePromotion', 'payload']));
    const planPricePerRebate = getPricePerRebate(campaign, plan);
    const lowestFee = Math.min(customCampaignFee, planPricePerRebate);
    const limit = isRewardRebate && !isResearchCampaign
        ? (campaign.get('rewardCodes')?.size || 0)
        : campaign.get('limit');
    const ccFee = plan.get('ccFeePercentage') * 0.01;
    const feePerRebate = Math.round(((rebateAmount * ccFee) + lowestFee) * 100) / 100;
    const totalPerRebate = rebateAmount + feePerRebate;
    const totalPerDay = Math.round(totalPerRebate * dailyLimit * 100) / 100;
    const totalPerCampaign = Math.round(totalPerRebate * limit * 100) / 100;
    const totalFees = Math.round(feePerRebate * limit * 100) / 100;
    const totalRebates = Math.round(rebateAmount * limit * 100) / 100;

    return CampaignCosts({
        baseFee: planPricePerRebate,
        dailyFees: feePerRebate * dailyLimit,
        dailyRebates: rebateAmount * dailyLimit,
        feePerSnag: feePerRebate,
        rebateAmount,
        totalFees,
        totalPerCampaign,
        totalPerDay,
        totalPerRebate,
        totalRebates,
    });
}

export function makePlural(amount, singular, plural) {
    if (amount === 1) {
        return singular;
    }

    return plural;
}

export function flattenQueryData(data, key) {
    return List(data.pages).map((page) => page[key]).flatten();
}

export function stripEmojis(text = '') {
    return text.replace(/([\u2700-\u27BF]|[\uE000-\uF8FF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|[\u2011-\u26FF]|\uD83E[\uDD10-\uDDFF])/g, '');
}
